// src/components/employee/AvailableShifts.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import {
  SwapHoriz as SwapIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import EmployeeMobileLayout from './EmployeeMobileLayout';

export default function AvailableShifts({ employee }) {
  const [availableShifts, setAvailableShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [claimDialog, setClaimDialog] = useState(false);
  const [claimReason, setClaimReason] = useState('');

  useEffect(() => {
    // Mock data - replace with Firebase query
    setAvailableShifts([
      {
        id: 1,
        date: 'Friday, July 25',
        startTime: '6:00 PM',
        endTime: '2:00 AM',
        role: 'Bartender',
        location: 'Downtown',
        department: 'Bar',
        originalEmployee: 'Mike Johnson',
        reason: 'Family emergency',
        postedAt: '2 hours ago',
        estimatedHours: 8
      },
      {
        id: 2,
        date: 'Saturday, July 26',
        startTime: '10:00 AM',
        endTime: '6:00 PM',
        role: 'Host',
        location: 'Uptown',
        department: 'Front of House',
        originalEmployee: 'Sarah Chen',
        reason: 'Personal appointment',
        postedAt: '4 hours ago',
        estimatedHours: 8
      },
      {
        id: 3,
        date: 'Sunday, July 27',
        startTime: '11:00 AM',
        endTime: '7:00 PM',
        role: 'Server',
        location: 'Downtown',
        department: 'Front of House',
        originalEmployee: 'Alex Rivera',
        reason: 'Vacation day',
        postedAt: '1 day ago',
        estimatedHours: 8
      },
      {
        id: 4,
        date: 'Monday, July 28',
        startTime: '2:00 PM',
        endTime: '10:00 PM',
        role: 'Cook',
        location: 'Downtown',
        department: 'Kitchen',
        originalEmployee: 'David Kim',
        reason: 'Sick day',
        postedAt: '3 hours ago',
        estimatedHours: 8
      }
    ]);
  }, []);

  const handleClaimShift = (shift) => {
    setSelectedShift(shift);
    setClaimDialog(true);
  };

  const handleSubmitClaim = () => {
    // Here you would submit the claim to Firebase
    
    // Close dialog and reset
    setClaimDialog(false);
    setClaimReason('');
    setSelectedShift(null);
    
    // Show success message (you could add a snackbar here)
    alert('Shift claim submitted! Awaiting manager approval.');
  };

  return (
    <EmployeeMobileLayout title="Available Shifts" employee={employee}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Pick Up Shifts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {availableShifts.length} shifts available for pickup
          </Typography>
        </Box>

        {/* Available Shifts List */}
        <Stack spacing={2}>
          {availableShifts.map((shift) => (
            <Card key={shift.id} elevation={2}>
              <CardContent>
                {/* Date and Time */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {shift.date}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TimeIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {shift.startTime} - {shift.endTime} ({shift.estimatedHours}h)
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {shift.postedAt}
                  </Typography>
                </Box>

                {/* Role and Location */}
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip 
                    icon={<WorkIcon />}
                    label={shift.role} 
                    color="primary" 
                    size="small" 
                  />
                  <Chip 
                    icon={<LocationIcon />}
                    label={shift.location} 
                    variant="outlined" 
                    size="small" 
                  />
                  <Chip 
                    label={shift.department} 
                    variant="outlined" 
                    size="small" 
                  />
                </Stack>

                {/* Original Employee and Reason */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    <strong>Originally scheduled:</strong> {shift.originalEmployee}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Reason:</strong> {shift.reason}
                  </Typography>
                </Box>

                {/* Action Button */}
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SwapIcon />}
                  onClick={() => handleClaimShift(shift)}
                  sx={{ mt: 1 }}
                >
                  Claim This Shift
                </Button>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {/* Empty State */}
        {availableShifts.length === 0 && (
          <Card elevation={1}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <SwapIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No Available Shifts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check back later for new shift opportunities
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Claim Shift Dialog */}
      <Dialog 
        open={claimDialog} 
        onClose={() => setClaimDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Claim Shift</DialogTitle>
        <DialogContent>
          {selectedShift && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {selectedShift.date}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {selectedShift.startTime} - {selectedShift.endTime} • {selectedShift.role}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedShift.location} • {selectedShift.department}
              </Typography>
            </Box>
          )}
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Your shift claim will be sent to management for approval. You'll be notified once it's approved or denied.
          </Alert>

          <TextField
            label="Why do you want this shift? (Optional)"
            multiline
            rows={3}
            fullWidth
            value={claimReason}
            onChange={(e) => setClaimReason(e.target.value)}
            placeholder="e.g., I'm available and could use the extra hours"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitClaim}
          >
            Submit Claim
          </Button>
        </DialogActions>
      </Dialog>
    </EmployeeMobileLayout>
  );
}
